import React, { useCallback, useEffect, useState } from "react";
import { MdCampaign, MdDoneAll } from "react-icons/md";
import { usePermissions } from "../../../auth/PermissionProvider";
import { Permissions } from "../../../auth/permissions";
import { NewsCategory, NewsFeedItem, NewsPost, NewsPostRequest } from "../../../interfaces/News";
import {
  createNewsPost,
  deleteNewsPost,
  getNewsFeed,
  getNewsPosts,
  markAllNewsRead,
  markNewsRead,
  updateNewsPost,
} from "../../../services/newsApi";
import NewsAdminList from "./components/NewsAdminList";
import NewsComposer from "./components/NewsComposer";
import NewsFeed from "./components/NewsFeed";

/**
 * Opera/Data News — o canal de comunicados da empresa.
 *
 * Uma tela, dois modos: todo colaborador lê o feed; quem tem `news.manage` também publica. Separar
 * em duas telas colocaria quem publica fora do lugar onde o comunicado é lido — e ninguém escreve
 * bem para um feed que não acompanha.
 */
export default function NewsPage() {
  const { can } = usePermissions();
  const podePublicar = can(Permissions.NewsManage);

  const [feed, setFeed] = useState<NewsFeedItem[]>([]);
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [filter, setFilter] = useState<NewsCategory | null>(null);
  const [editing, setEditing] = useState<NewsPost | null>(null);
  const [tab, setTab] = useState<"feed" | "manage">("feed");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ t: "ok" | "err"; m: string } | null>(null);

  const flash = (t: "ok" | "err", m: string) => {
    setMsg({ t, m });
    setTimeout(() => setMsg(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      setFeed(await getNewsFeed());
      if (podePublicar) setPosts(await getNewsPosts());
    } catch {
      flash("err", "Não foi possível carregar os comunicados.");
    } finally {
      setLoading(false);
    }
  }, [podePublicar]);

  useEffect(() => {
    void load();
  }, [load]);

  // Otimista: a leitura é irrelevante para o servidor se falhar (o post volta como não lido).
  const onRead = async (id: number) => {
    setFeed((f) => f.map((i) => (i.id === id ? { ...i, read: true } : i)));
    try {
      await markNewsRead(id);
    } catch {
      /* silencioso — reaparece como não lido no próximo load */
    }
  };

  const onReadAll = async () => {
    setFeed((f) => f.map((i) => ({ ...i, read: true })));
    try {
      await markAllNewsRead();
    } catch {
      flash("err", "Não deu para marcar como lido.");
    }
  };

  const onSubmit = async (req: NewsPostRequest, publish: boolean) => {
    setSaving(true);
    try {
      if (editing) await updateNewsPost(editing.id, req);
      else await createNewsPost(req);
      setEditing(null);
      await load();
      flash(
        "ok",
        publish
          ? "Publicado. Aparece no portal agora e nos widgets em até 10 minutos."
          : "Rascunho salvo — ninguém vê até você publicar."
      );
    } catch {
      flash("err", "Falha ao salvar o comunicado.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (post: NewsPost) => {
    if (!window.confirm(`Excluir "${post.title}"? Ele some do feed de todo mundo.`)) return;
    try {
      await deleteNewsPost(post.id);
      if (editing?.id === post.id) setEditing(null);
      await load();
      flash("ok", "Comunicado excluído.");
    } catch {
      flash("err", "Falha ao excluir.");
    }
  };

  const naoLidos = feed.filter((i) => !i.read).length;

  return (
    <div className="mt-5 flex flex-col gap-5 animate-fade-up">
      <header className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="dv-aura flex h-11 w-11 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
            <MdCampaign className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <span className="inline-flex items-center rounded-full bg-dv-blue-500/10 px-3 py-1 font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
              Comunicação interna
            </span>
            <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight text-navy-700 dark:text-white">
              Opera/Data News
            </h1>
          </div>
        </div>

        {naoLidos > 0 && (
          <button
            type="button"
            onClick={onReadAll}
            className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            <MdDoneAll className="h-4 w-4" aria-hidden="true" />
            Marcar {naoLidos} como {naoLidos === 1 ? "lido" : "lidos"}
          </button>
        )}
      </header>

      <p className="max-w-2xl text-sm text-gray-600 dark:text-gray-300">
        Novidades e comunicados para toda a empresa. O que é publicado aqui chega também ao{" "}
        <b>widget instalado nos notebooks</b> — com o contador de não lidos na bolha.
      </p>

      {msg && (
        <div
          role="alert"
          className={`rounded-xl px-4 py-2.5 text-sm ${
            msg.t === "ok"
              ? "border border-green-200 bg-green-50 text-green-700 dark:border-green-500/30 dark:bg-green-900/20 dark:text-green-300"
              : "border border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
          }`}
        >
          {msg.m}
        </div>
      )}

      {podePublicar && (
        <div className="flex gap-2">
          {(
            [
              ["feed", "Feed"],
              ["manage", "Publicar"],
            ] as const
          ).map(([key, texto]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${
                tab === key
                  ? "bg-ink-900 text-white dark:bg-white dark:text-ink-900"
                  : "border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
              }`}
            >
              {texto}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="animate-pulse rounded-2xl border border-gray-200 p-10 text-center text-gray-400 dark:border-white/10">
          Carregando…
        </div>
      ) : tab === "feed" || !podePublicar ? (
        <NewsFeed items={feed} filter={filter} onFilter={setFilter} onRead={onRead} />
      ) : (
        <div className="flex flex-col gap-5">
          <NewsComposer
            editing={editing}
            saving={saving}
            onSubmit={onSubmit}
            onCancel={() => setEditing(null)}
          />
          <NewsAdminList posts={posts} onEdit={setEditing} onDelete={onDelete} />
        </div>
      )}
    </div>
  );
}
